import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PageWrapper from "../../components/layout/PageWrapper";
import {
  BookOpen,
  Palette,
  Server,
  ArrowUpRight,
  FileCode,
  Briefcase,
  LayoutDashboard,
  Globe,
  Package,
  ShoppingBag,
  Truck,
  Github,
  ChevronDown,
  Smartphone,
} from "lucide-react";


// Tinted icon tiles (soft background + colored icon) per accent.
const TINT = {
  "from-indigo-500 to-violet-600": "bg-violet-400/10 text-violet-400",
  "from-emerald-600 to-teal-700": "bg-ok-soft text-ok",
  "from-blue-500 to-cyan-600": "bg-info-soft text-info",
  "from-emerald-500 to-green-600": "bg-ok-soft text-ok",
};
const tintOf = (accent) => TINT[accent] || "bg-accent-soft text-accent-ink";

const SPLASH_DURATION_MS = 320;

// Documentation sources – URLs can be overridden via .env
const DOCUMENTATION_SOURCES = [
  {
    id: "frontend",
    title: "Frontend Documentation",
    description:
      "Next.js, React 19, TypeScript, and modern frontend practices. Setup, guides, and architecture.",
    url:
      process.env.REACT_APP_FRONTEND_DOCS_URL ||
      "https://abs-frontend-docs.vercel.app/",
    icon: Palette,
    accent: "from-indigo-500 to-violet-600",
  },
  {
    id: "backend",
    title: "Backend Documentation",
    description:
      "API reference, database schema, server setup, and deployment guides.",
    url: process.env.REACT_APP_BACKEND_DOCS_URL || "#",
    icon: Server,
    accent: "from-emerald-600 to-teal-700",
    comingSoon: true,
  },
  {
    id: "general",
    title: "Project Documentation",
    description:
      "Architecture overview, conventions, and onboarding resources.",
    url: process.env.REACT_APP_PROJECT_DOCS_URL || "#",
    icon: BookOpen,
    accent: "from-blue-500 to-cyan-600",
    comingSoon: true,
  },
];

// References – API docs grouped by environment
const REFERENCES_SOURCES = [
  {
    id: "api-production",
    title: "Production API Docs",
    description: "Production environment API documentation",
    icon: Server,
    accent: "from-emerald-500 to-green-600",
    isExpandable: true,
    links: [
      {
        id: "prod-employee",
        title: "Employee",
        url: "https://core.absegy.com/docs/employee",
        icon: FileCode,
      },
      {
        id: "prod-client",
        title: "Client Portal",
        url: "https://core.absegy.com/docs/client",
        icon: FileCode,
      },
      {
        id: "prod-courier",
        title: "Courier App",
        url: "https://core.absegy.com/docs/courier",
        icon: FileCode,
      },
    ],
  },
  {
    id: "api-development",
    title: "Development API Docs",
    description: "Development environment API documentation",
    icon: FileCode,
    accent: "from-blue-500 to-indigo-600",
    isExpandable: true,
    links: [
      {
        id: "dev-employee",
        title: "Employee",
        url: "https://dev.backend.absegy.com/docs/employee",
        icon: FileCode,
      },
      {
        id: "dev-client",
        title: "Client Portal",
        url: "https://dev.backend.absegy.com/docs/client",
        icon: FileCode,
      },
      {
        id: "dev-courier",
        title: "Courier App",
        url: "https://dev.backend.absegy.com/docs/courier",
        icon: FileCode,
      },
      {
        id: "dev-Auth",
        title: "Auth",
        url: "https://dev.backend.absegy.com/docs/auth",
        icon: FileCode,
      },
      {
        id: "dev-Profile",
        title: "profile",
        url: "https://dev.backend.absegy.com/docs/profile",
        icon: FileCode,
      },
    ],
  },
  {
    id: "github",
    title: "GitHub Organization",
    description: "Company GitHub organization and repositories.",
    url: "https://github.com/ABS-Courier",
    icon: Github,
    accent: "from-slate-600 to-gray-700",
  },
];

// Active company projects
const ACTIVE_PROJECTS = [
  {
    id: "ops",
    title: "Operations & Internal Management System",
    description: "System used for internal operations and organization.",
    url: "https://ops.absegy.com/",
    icon: Briefcase,
    accent: "from-violet-500 to-purple-600",
  },
  {
    id: "portal",
    title: "Client Portal",
    description: "Portal used by clients.",
    url: "https://portal.absegy.com/",
    icon: LayoutDashboard,
    accent: "from-amber-500 to-orange-600",
  },

  {
    id: "portfolio",
    title: "Company Portfolio Website",
    description: "Company public portfolio website.",
    url: "https://www.absegy.com/en",
    icon: Globe,
    accent: "from-cyan-500 to-blue-600",
  },
  {
    id: "buyandfly",
    title: "Buy and Fly",
    description: "Global shopping & shipping service.",
    url: "https://buyandfly.absegy.com/en",
    icon: ShoppingBag,
    accent: "from-pink-500 to-rose-600",
  },
  {
    id: "intl",
    title: "International Shipping",
    description: "Global freight & logistics solutions.",
    url: "https://intl.absegy.com/",
    icon: Truck,
    accent: "from-indigo-500 to-blue-600",
  },
  {
    id: "bank-masr",
    title: "Bank Masr",
    description: "Bank Masr system.",
    url: "https://bm.absegy.com/en/auth/signin",
    icon: Briefcase,
    accent: "from-emerald-500 to-green-600",
  },
  {
    id: "courier-app",
    title: "Courier App",
    description: "Mobile application for courier services.",
    icon: Package,
    accent: "from-violet-500 to-purple-600",
    isExpandable: true,
    links: [
      {
        id: "google-play",
        title: "Google Play",
        url: "https://play.google.com/store/apps/details?id=com.absegy.courier",
        icon: Smartphone,
      },
      {
        id: "app-store",
        title: "App Store",
        url: "https://apps.apple.com/us/app/abs-runner/id6758909389",
        icon: Smartphone,
      },
    ],
  },
];

const DocCard = ({
  item,
  index,
  baseDelay,
  handleCardClick,
  linkLabel = "Open docs",
  expandedId,
  onToggleExpand,
}) => {
  const Icon = item.icon;
  const isComingSoon = item.comingSoon;
  const isExpandable = item.isExpandable && item.links && item.links.length > 0;
  const isExpanded = expandedId === item.id;

  const handleClick = (e) => {
    if (isComingSoon) return;
    if (isExpandable) {
      onToggleExpand(item.id);
    } else {
      handleCardClick(e, item.url);
    }
  };

  return (
    <motion.div
      key={item.id}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (isComingSoon) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick(e);
        }
      }}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: baseDelay + index * 0.08,
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      whileHover={
        !isComingSoon
          ? {
              y: -6,
              transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] },
            }
          : {}
      }
      whileTap={!isComingSoon ? { scale: 0.98 } : {}}
      className={`group block ${isComingSoon ? "cursor-default" : "cursor-pointer"}`}
      aria-label={
        isComingSoon
          ? `${item.title} (Coming soon)`
          : isExpandable
            ? `Toggle ${item.title}`
            : `Open ${item.title}`
      }
    >
      <div
        className={`relative h-full overflow-hidden rounded-card border border-line bg-panel backdrop-blur-xl shadow-card p-5 transition-all duration-300 ${!isComingSoon ? "group-hover:border-accent-line" : ""}`}
      >
        {isComingSoon && (
          <span className="absolute top-5 right-5 inline-flex items-center h-6 px-2.5 rounded-full border border-dashed border-line2 text-[11.5px] text-faint">
            Coming soon
          </span>
        )}
        <div className="mb-5 flex">
          <div className={`w-10 h-10 rounded-xl ${tintOf(item.accent)} flex items-center justify-center`}>
            <Icon
              className="w-5 h-5"
              strokeWidth={1.75}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </div>
        </div>
        <h3 className="font-display text-[16px] font-semibold text-ink mb-2 whitespace-nowrap truncate">
          {item.title}
        </h3>
        {item.description && (
          <p className="text-[13.5px] text-muted leading-relaxed mb-4 flex-1">
            {item.description}
          </p>
        )}
        <span
          className={`inline-flex items-center gap-2 text-sm font-medium transition-colors duration-200 ${isComingSoon ? "text-faint" : "text-accent-ink group-hover:text-accent"}`}
        >
          {isComingSoon
            ? "Not available yet"
            : isExpandable
              ? isExpanded
                ? "Collapse"
                : "View options"
              : linkLabel}
          {!isComingSoon &&
            (isExpandable ? (
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
              </motion.div>
            ) : (
              <ArrowUpRight
                className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1"
                strokeWidth={2.5}
              />
            ))}
        </span>

        {/* Expandable Links Section */}
        <AnimatePresence>
          {isExpandable && isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="overflow-hidden mt-4"
            >
              <div className="space-y-2 pt-3 border-t border-gray-200/50 dark:border-dark-100/50">
                {item.links.map((link, linkIndex) => {
                  const LinkIcon = link.icon;
                  const linkComingSoon = link.comingSoon;
                  return (
                    <motion.div
                      key={link.id}
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: linkIndex * 0.05 }}
                      role="button"
                      tabIndex={linkComingSoon ? -1 : 0}
                      onClick={(e) => {
                        if (linkComingSoon) return;
                        e.stopPropagation();
                        handleCardClick(e, link.url);
                      }}
                      onKeyDown={(e) => {
                        if (linkComingSoon) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          handleCardClick(e, link.url);
                        }
                      }}
                      className={`group/link relative flex items-center gap-3 rounded-xl bg-panel2 border border-line px-4 py-3 transition-colors duration-200 ${
                        linkComingSoon ? "cursor-default" : "hover:bg-hover cursor-pointer"
                      }`}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-accent-soft flex items-center justify-center">
                        <LinkIcon
                          className="w-4 h-4 text-accent-ink"
                          strokeWidth={2}
                        />
                      </div>
                      <span className="flex-1 text-sm font-medium text-ink">
                        {link.title}
                      </span>
                      {linkComingSoon && (
                        <span className="inline-flex items-center h-6 px-2.5 rounded-full border border-dashed border-line2 text-[11.5px] text-faint">
                          Coming soon
                        </span>
                      )}
                      {!linkComingSoon && (
                        <ArrowUpRight
                          className="flex-shrink-0 w-4 h-4 text-primary-accent opacity-0 group-hover/link:opacity-100 transition-opacity"
                          strokeWidth={2}
                        />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const ReferenceCard = ({
  item,
  index,
  baseDelay,
  handleCardClick,
  descriptionContent,
  expandedId,
  onToggleExpand,
}) => {
  const Icon = item.icon;
  const isExpanded = expandedId === item.id;
  const isExpandable = item.isExpandable && item.links && item.links.length > 0;

  const handleClick = (e) => {
    if (isExpandable) {
      onToggleExpand(item.id);
    } else {
      handleCardClick(e, item.url);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -16 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        delay: baseDelay + index * 0.06,
        duration: 0.35,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className="overflow-hidden"
    >
      <motion.div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e);
          }
        }}
        whileTap={{ scale: 0.995 }}
        className="group relative flex items-center gap-4 px-5 py-4 hover:bg-hover transition-colors cursor-pointer"
        aria-label={
          isExpandable ? `Toggle ${item.title}` : `Open ${item.title}`
        }
      >
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${tintOf(item.accent)} flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[14.5px] font-medium text-ink truncate">
            {item.title}
          </h3>
          {(descriptionContent || item.description) && (
            <p
              className={`text-[12.5px] text-faint mt-0.5 flex items-center gap-1 flex-wrap ${!descriptionContent ? "truncate" : ""}`}
            >
              {descriptionContent || item.description}
            </p>
          )}
        </div>
        {isExpandable ? (
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
            className="flex-shrink-0"
          >
            <ChevronDown
              className="w-4 h-4 text-faint"
              strokeWidth={2}
            />
          </motion.div>
        ) : (
          <ArrowUpRight
            className="flex-shrink-0 w-4 h-4 text-faint group-hover:text-accent transition-colors"
            strokeWidth={2}
          />
        )}
      </motion.div>

      {/* Expandable Links Section */}
      <AnimatePresence>
        {isExpandable && isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 space-y-2">
              {item.links.map((link, linkIndex) => {
                const LinkIcon = link.icon;
                return (
                  <motion.div
                    key={link.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: linkIndex * 0.05 }}
                    role="button"
                    tabIndex={0}
                    onClick={(e) => handleCardClick(e, link.url)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCardClick(e, link.url);
                      }
                    }}
                    className="group/link flex items-center gap-3 rounded-xl bg-panel2 hover:bg-hover border border-line px-4 py-3 transition-colors duration-200 cursor-pointer"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-accent-soft flex items-center justify-center">
                      <LinkIcon
                        className="w-4 h-4 text-accent-ink"
                        strokeWidth={2}
                      />
                    </div>
                    <span className="flex-1 text-sm font-medium text-ink">
                      {link.title}
                    </span>
                    <ArrowUpRight
                      className="flex-shrink-0 w-4 h-4 text-primary-accent opacity-0 group-hover/link:opacity-100 transition-opacity"
                      strokeWidth={2}
                    />
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const SectionHeader = ({ title, subtitle }) => (
  <div className="mb-3.5">
    <h2 className="font-display text-[17px] font-semibold text-ink whitespace-nowrap">
      {title}
    </h2>
    {subtitle && (
      <p className="text-[13px] text-faint mt-0.5">
        {subtitle}
      </p>
    )}
  </div>
);

const Documentation = () => {
  const [transitioningUrl, setTransitioningUrl] = useState(null);
  const [expandedReferenceId, setExpandedReferenceId] = useState(null);
  const [expandedProjectId, setExpandedProjectId] = useState(null);

  const handleCardClick = useCallback((e, url) => {
    if (!url || url === "#") return;
    e.preventDefault();
    setTransitioningUrl(url);
    setTimeout(() => {
      window.open(url, "_blank", "noopener,noreferrer");
      setTransitioningUrl(null);
    }, SPLASH_DURATION_MS);
  }, []);

  const handleToggleExpand = useCallback((id) => {
    setExpandedReferenceId((prev) => (prev === id ? null : id));
  }, []);

  const handleToggleProjectExpand = useCallback((id) => {
    setExpandedProjectId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <PageWrapper
      title="Documentation"
      subtitle="Central hub for documentation, references, and active projects"
    >
      {/* Splash transition overlay */}
      <AnimatePresence>
        {transitioningUrl && (
          <motion.div
            key="splash"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 dark:bg-black/60 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.02, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="flex flex-col items-center gap-4"
            >
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 rounded-full border-2 border-primary-accent/20" />
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary-accent"
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                />
              </div>
              <span className="text-xs font-medium text-primary-accent tracking-widest uppercase">
                Opening
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-10">
        {/* 1. References Section – compact list layout */}
        <section>
          <SectionHeader
            title="References"
            subtitle="API docs and internal resources"
          />
          <div className="card !p-0 overflow-hidden divide-y divide-[var(--line)]">
            {REFERENCES_SOURCES.map((item, index) => (
              <ReferenceCard
                key={item.id}
                item={item}
                index={index}
                baseDelay={0}
                handleCardClick={handleCardClick}
                expandedId={expandedReferenceId}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </div>
        </section>

        {/* 2. Documentation Section – original card design (unchanged) */}
        <section>
          <SectionHeader
            title="Documentation"
            subtitle="Development guides and technical documentation"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DOCUMENTATION_SOURCES.map((doc, index) => (
              <DocCard
                key={doc.id}
                item={doc}
                index={index}
                baseDelay={0.05}
                handleCardClick={handleCardClick}
                linkLabel="Open docs"
              />
            ))}
          </div>
        </section>

        {/* 3. Active Projects Section – same design as Documentation */}
        <section>
          <SectionHeader
            title="Active Projects"
            subtitle="Currently active projects"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ACTIVE_PROJECTS.map((item, index) => (
              <DocCard
                key={item.id}
                item={item}
                index={index}
                baseDelay={0.1}
                handleCardClick={handleCardClick}
                linkLabel="Open project"
                expandedId={expandedProjectId}
                onToggleExpand={handleToggleProjectExpand}
              />
            ))}
          </div>
        </section>
      </div>
    </PageWrapper>
  );
};

export default Documentation;
